/**
 * studio/src/lib/gtm-bootstrap.ts
 *
 * PFP-1 §3.5 — the GTM container bootstrap. Imported as the
 * FIRST module in `src/main.ts` so the GTM container is
 * registered before any UI mounts and any PFP event can
 * reach it via `window.dataLayer.push(...)`.
 *
 * Why a separate module:
 *   - The GTM container snippet must run before any
 *     dataLayer.push() call. Importing this module at the
 *     top of main.ts makes that ordering structural — the
 *     TypeScript module graph guarantees the snippet runs
 *     before any code that consumes dataLayer.
 *   - The container ID is a build-time env var
 *     (VITE_GTM_CONTAINER_ID). Reading it in a module-level
 *     `if` lets Vite dead-code-eliminate the whole snippet
 *     in dev (when the env var is unset).
 *   - The shape follows Google's official "Google Tag
 *     Manager — Quickstart" snippet (gtm.js, async,
 *     push-to-dataLayer first). This is the shape the
 *     Matomo tag inside the GTM container expects.
 *
 * The Matomo tag's `trackerUrl` and `siteId` are
 * GTM container variables, not front-end code. The
 * container export is `web-public/gtm-container.json`
 * (see `www/src/__tests__/matomo-tag.test.ts`).
 *
 * See:
 *   - .cursor/plans/path-to-first-pitch/sprint-1-easy-first-pitch-template.md §3.5
 *   - .cursor/plans/path-to-first-pitch/data-model-events.md §1.1
 */
const GTM_CONTAINER_ID = (import.meta.env.VITE_GTM_CONTAINER_ID as string) ?? "";

if (GTM_CONTAINER_ID) {
  // SIDE-EFFECT: initialize the GTM dataLayer + load gtm.js.
  // This code path is the ONLY way the studio's HTML gets the
  // GTM container. It must run synchronously at module load
  // (before any dataLayer.push) so the first PFP event in
  // the funnel is captured.
  ((): void => {
    const w = window as unknown as {
      dataLayer: unknown[];
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const f = document.getElementsByTagName("script")[0];
    const j = document.createElement("script") as HTMLScriptElement;
    const dl = "dataLayer" !== "dataLayer" ? "&l=dataLayer" : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + GTM_CONTAINER_ID + dl;
    if (f && f.parentNode) {
      f.parentNode.insertBefore(j, f);
    } else {
      // No <script> tag yet — append to head.
      document.head.appendChild(j);
    }
  })();
} else {
  // Dev-mode no-op. The contract test in
  // www/src/__tests__/gtm-container.test.ts catches a missing
  // env var in production deploys. In dev, pushStudioEvent
  // also no-ops + logs a console warning (see gtm-events.ts).
  // eslint-disable-next-line no-console
  console.warn(
    "[gtm-bootstrap] VITE_GTM_CONTAINER_ID is unset; GTM container not loaded (PFP-1 §3.5 dev-mode no-op).",
  );
}

export { GTM_CONTAINER_ID };
